import type { DatasetOriginDto } from '~/backend/connector-next-api/connector-next-schemas';
import type {
    DatasetOriginKind,
    RecordedDatasetOrigin,
    RecordedUser,
} from '~/domain/provenance/RecordedDatasetOrigin';

const KNOWN_KINDS: ReadonlySet<string> = new Set<DatasetOriginKind>([
    'AiTask',
    'CreatedInApp',
    'Duplicate',
    'Imported',
]);

function toKind(kind: string | null | undefined): DatasetOriginKind {
    return kind && KNOWN_KINDS.has(kind)
        ? (kind as DatasetOriginKind)
        : 'Unrecognised';
}

/**
 * A user as recorded. Only `Authenticated` counts as verified: an absent or
 * unknown source is treated as unverified rather than trusted.
 */
export function toRecordedUser(
    name: string | null | undefined,
    source: string | null | undefined
): RecordedUser | null {
    return name ? { name, verified: source === 'Authenticated' } : null;
}

/** A recorded time; null when absent or not a time. */
export function toRecordedDate(value: string | null | undefined): Date | null {
    if (!value) {
        return null;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Maps the connector's recorded origin into the domain shape. `undefined` /
 * `null` in, null out: no origin was recorded. An origin without a usable
 * `savedAt` is malformed and also maps to null rather than being shown with
 * an invented time.
 */
export function toRecordedDatasetOrigin(
    dto: DatasetOriginDto | null | undefined
): RecordedDatasetOrigin | null {
    if (!dto) {
        return null;
    }
    const savedAt = toRecordedDate(dto.savedAt);
    if (!savedAt) {
        return null;
    }
    return {
        kind: toKind(dto.kind),
        model: dto.model ?? null,
        modelLabel: dto.modelLabel ?? null,
        modelResultKept: dto.modelResultKept === true,
        modelVersion: dto.modelVersion ?? null,
        modelVersionConfirmed: dto.modelVersionSource === 'BackendConfirmed',
        modelCustomization: dto.modelCustomization ?? null,
        requestedStructures: dto.requestedStructures ?? [],
        requestedBy: toRecordedUser(dto.requestedBy, dto.requestedBySource),
        requestedAt: toRecordedDate(dto.requestedAt),
        generatedAt: toRecordedDate(dto.generatedAt),
        savedBy: toRecordedUser(dto.savedBy, dto.savedBySource),
        savedAt,
        derivedFromDatasetId: dto.derivedFromDatasetId ?? null,
        importSource: dto.importSource ?? null,
        declaredManufacturer: dto.declaredManufacturer ?? null,
        declaredModelName: dto.declaredModelName ?? null,
        declaredSoftwareVersions: dto.declaredSoftwareVersions ?? null,
    };
}
